import { Search, Map, Wrench, TrendingUp, ChevronRight } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const methIcons = [Search, Map, Wrench, TrendingUp];

function MethodologyStrip({ isVisible, methodology }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800/40 mb-20 border border-slate-800/40">
      {methodology.map((step, i) => {
        const Icon = methIcons[i];
        return (
          <div key={i} className="bg-[#0F1A2E] p-6 lg:p-8 relative group hover:bg-[#1E3455] transition-colors duration-300"
            style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.6s ease-out, transform 0.6s ease-out, background-color 0.3s", transitionDelay: `${i * 100}ms` }}>
            <div className="flex items-start gap-3 mb-3">
              <span className="text-[#D4AF37]/30 text-xs font-bold tracking-widest">{step.phase}</span>
              {i < methodology.length - 1 && <ChevronRight size={12} className="text-slate-700 absolute right-3 top-1/2 -translate-y-1/2 hidden lg:block" />}
            </div>
            <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
              <Icon size={15} className="text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h4 className="text-white text-sm font-semibold mb-2">{step.label}</h4>
            <p className="text-slate-300 text-xs leading-relaxed">{step.desc}</p>
          </div>
        );
      })}
    </div>
  );
}

const sectorAccents = {
  "Mining & Resources": "#F59E0B",
  "Steel & Metals": "#D4AF37",
  "Steel & Manufacturing": "#3B82F6",
  "Mines & Ressources": "#F59E0B",
  "Acier & M\u00e9taux": "#D4AF37",
  "Acier & Industrie": "#3B82F6",
};

function CaseStudyCard({ cs, index, isVisible, challengeLabel, approachLabel }) {
  const accent = sectorAccents[cs.sector] || "#D4AF37";
  return (
    <div data-testid={`case-study-${index}`}
      className="group overflow-hidden relative"
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0) scale(1)" : "translateY(36px) scale(0.96)", transition: "opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)", transitionDelay: `${200 + index * 180}ms` }}>
      {/* Top accent line with sector color */}
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      <div className="card-hover-lift bg-[#162A45]/80 backdrop-blur-sm border border-slate-800/60 hover:border-slate-700/60 transition-all duration-500">
        <div className="p-8 lg:p-10">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
                <span className="text-xs uppercase tracking-widest font-medium" style={{ color: accent }}>{cs.sector}</span>
              </div>
              <h3 className="text-white text-xl md:text-2xl leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{cs.project}</h3>
              <p className="text-slate-300 text-xs mt-1.5">{cs.location}</p>
            </div>
            {/* Metrics as compact badges on desktop */}
            <div className="hidden lg:flex gap-6">
              {cs.metrics.map((m, i) => (
                <div key={i} className="text-right">
                  <div className="text-2xl font-bold tracking-tight" style={{ color: accent, fontFamily: "'Space Grotesk', sans-serif" }}>{m.value}</div>
                  <div className="text-slate-300 text-[10px] uppercase tracking-wider mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-2">{challengeLabel}</p>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">{cs.challenge}</p>
              <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-3">{approachLabel}</p>
              <ul className="space-y-2">
                {cs.approach.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: accent }} />{p}
                  </li>
                ))}
              </ul>
            </div>
            {/* Metrics on mobile */}
            <div className="flex flex-col justify-center gap-4 lg:hidden">
              {cs.metrics.map((m, i) => (
                <div key={i} className="border-l-2 pl-4 transition-colors duration-500" style={{ borderColor: `${accent}40` }}>
                  <div className="text-2xl font-bold tracking-tight" style={{ color: accent, fontFamily: "'Space Grotesk', sans-serif" }}>{m.value}</div>
                  <div className="text-slate-300 text-xs mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useLanguage();
  const cs = t.caseStudies;

  return (
    <section id="results" data-testid="case-studies-section" ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      {/* Distinctive background - diagonal gradient + subtle image overlay */}
      <div className="absolute inset-0 bg-[#0F1A2E]" />
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1748946469841-99e75941b997?w=1920&q=30')", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0F1A2E] via-[#0F1A2E]/95 to-[#1E3455]" />
      {/* Decorative diagonal line */}
      <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-[#D4AF37]/10 via-[#D4AF37]/5 to-transparent transform rotate-12 origin-top-right" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16 transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(20px)" }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{cs.label}</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="text-4xl md:text-5xl text-white max-w-xl leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {cs.heading}<br /><span className="gold-gradient-text">{cs.headingAccent}</span>
            </h2>
            <p className="text-slate-300 text-sm max-w-sm leading-relaxed">{cs.desc}</p>
          </div>
        </div>
        <MethodologyStrip isVisible={isVisible} methodology={cs.methodology} />
        <div className="space-y-8">
          {cs.items.map((item, i) => (
            <CaseStudyCard key={i} cs={item} index={i} isVisible={isVisible} challengeLabel={cs.challengeLabel} approachLabel={cs.approachLabel} />
          ))}
        </div>
      </div>
    </section>
  );
}
